import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getInvestorApiUrl } from '../../utils/config';
import { ApiRequestParams, handleFetchApi } from '../../utils/api';

// Define types for state and response data
interface FundsData {
    availableFund: number;
    totalFund: number;
    lockedFund: number;
}

export interface TransactionHistoryItem {
    id: string;
    transactionType: string;
    amount: number;
    date: string;
    status: string;
    description?: string;
    Type?: string;
    CompletedAt?: string;
    CreatedAt?: string;
    initiatedAt?: string;
}

interface WithdrawalHistoryItem {
    id: string;
    transactionType: string;
    amount: number;
    status: string;
    date: string;

}

interface FundResponse {
    body: {
        availabelBalance: number;
        totalBalance: number;
        lockedBalance: number;
    };
}

interface TransactionHistoryResponse {
    body?: TransactionHistoryItem[];
    responsData?: any;
    history?: TransactionHistoryItem[]
}

interface WithdrawalHistoryResponse {
    body?: TransactionHistoryItem[];
    responsData?: any;
    history?: TransactionHistoryItem[]
}

interface FundState {
    fundsData: FundsData;
    transactionHistory: TransactionHistoryItem[];
    withdrawalHistory: WithdrawalHistoryItem[];
    loading: boolean;
    loadingCancel:boolean;
    error: string;
    fundResponse: FundResponse | null;
    transactionResponse: TransactionHistoryResponse | null;
    cancelRequestResponse: any;
    addFundResponse: string | null;
    withdrawalResponse: string | null;
    withdrawalHistoryResponse: WithdrawalHistoryResponse | null;
    historyLoading: boolean;
}

const initialState: FundState = {
    fundsData: {
        availableFund: 0,
        totalFund: 0,
        lockedFund: 0,
    },
    transactionHistory: [],
    withdrawalHistory: [],
    loading: false,
    loadingCancel:false,
    error: "",
    fundResponse: null,
    transactionResponse: null,
    cancelRequestResponse: null,
    addFundResponse: "",
    withdrawalResponse: "",
    withdrawalHistoryResponse: null,
    historyLoading: false,
};

// Async thunks
export const getFund = createAsyncThunk<FundResponse, string>(
    "fund/getFund",
    async (investorId, thunkApi) => {
        try {
            const params: ApiRequestParams = {
                baseUrl: getInvestorApiUrl(),
                endpoint: `Ledger?investorId=${investorId}`,
            }
            const response = await handleFetchApi(params);
            // const response = await axios.get(`${baseUrl}/Ledger?investorId=${investorId}`);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response?.data || "An error occurred");
        }
    }
);

export const getTransactionHistory = createAsyncThunk<TransactionHistoryResponse, string>(
    "fund/getHistory",
    async (investorId: string, thunkApi) => {
        try {
            const params: ApiRequestParams = {
                baseUrl: getInvestorApiUrl(),
                endpoint: `transaction-history?investorId=${investorId}`,
            }
            const response = await handleFetchApi(params);
            // const response = await axios.get(`${baseUrl}/transaction-history?investorId=${investorId}`);
            return { responsData: response.data, history: response.data.body };
        } catch (error) {
            return thunkApi.rejectWithValue(error || "An error occurred");
        }
    }
);

export const getWithdrawalHistory = createAsyncThunk<WithdrawalHistoryResponse, string>(
    "fund/getWithdrawalHistory",
    async (investorId: string, thunkApi) => {
        try {
            const params: ApiRequestParams = {
                baseUrl: getInvestorApiUrl(),
                endpoint: `pending-transaction?investorId=${investorId}`,
            }
            const response = await handleFetchApi(params);
            // const response = await axios.get(`${baseUrl}/pending-transaction?investorId=${investorId}`);
            const withdrawalHistory = response.data.body.filter(
                (item: any) => item.transactionType === "WITHDRAWAL"
            );
            return { responsData: response.data, history: withdrawalHistory };
        } catch (error) {
            return thunkApi.rejectWithValue(error || "An error occurred");
        }
    }
);

export const addFund = createAsyncThunk<string, any>(
    "fund/addFund",
    async (payload, thunkApi) => {
        try {
            const params: ApiRequestParams = {
                baseUrl: getInvestorApiUrl(),
                endpoint: `Ledger`,
                payload,
                method: "POST"
            }
            const response = await handleFetchApi(params);
            // const response = await axios.post(`${baseUrl}/Ledger`, payload);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(
                error.response?.data || "Failed to post ledger configuration"
            );
        }
    }
);
export const addWireFund = createAsyncThunk<string, any>(
    "fund/addFundWire",
    async ({
        investorId,
        amount,
        formData
    }, thunkApi) => {
        try {
            const params: ApiRequestParams = {
                baseUrl: getInvestorApiUrl(),
                endpoint: `Ledger/manual/${investorId}?amount=${amount}`,
                payload: formData,
                method: "POST"
            }
            const response = await handleFetchApi(params);
            // const response = await axios.post(`${baseUrl}/Ledger`, payload);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(
                error.response?.data || "Failed to post ledger configuration"
            );
        }
    }
);

export const withdrawalFund = createAsyncThunk<string, any>(
    "fund/withdrawalFund",
    async (payload, thunkApi) => {
        try {
            const params: ApiRequestParams = {
                baseUrl: getInvestorApiUrl(),
                endpoint: `Ledger/withdrawals`,
                payload,
                method: "POST"
            }
            const response = await handleFetchApi(params);
            // const response = await axios.post(`${baseUrl}/Ledger/withdrawals`, payload);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(
                error.response?.data || "Failed to post ledger configuration"
            );
        }
    }
);

export const cancelWithdrawalFundRequest = createAsyncThunk<any, any>(
    "fund/cancelWithdrawalFund",
    async (payload, thunkApi) => {
        try {
            const params: ApiRequestParams = {
                baseUrl: getInvestorApiUrl(),
                endpoint: `update-transaction`,
                payload,
                method: "POST"
            }
            const response = await handleFetchApi(params);
            // const response = await axios.post(`${baseUrl}/update-transaction`, payload);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(
                error.response?.data || "Failed to post ledger configuration"
            );
        }
    }
);

// Redux slice
export const fundSlice = createSlice({
    name: 'fund',
    initialState,
    reducers: {
        setReduxLoading: (state, action) => {
            state.historyLoading = action.payload
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getFund.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.fundResponse = null;
            })
            .addCase(getFund.fulfilled, (state, action: PayloadAction<FundResponse>) => {
                state.loading = false;
                state.fundResponse = action.payload;
                state.fundsData.availableFund = action.payload?.body?.availabelBalance;
                state.fundsData.totalFund = action.payload?.body?.totalBalance;
                state.fundsData.lockedFund = action.payload?.body?.lockedBalance;
            })
            .addCase(getFund.rejected, (state) => {
                state.loading = false;
                state.error = "Failed to register user";
                state.fundResponse = null;
                state.fundsData.availableFund = 0;
                state.fundsData.totalFund = 0;
                state.fundsData.lockedFund = 0;
            })
            .addCase(getTransactionHistory.pending, (state) => {
                state.historyLoading = true;
                state.error = "";
                state.transactionResponse = null;
            })
            .addCase(getTransactionHistory.fulfilled, (state, action: PayloadAction<TransactionHistoryResponse>) => {
                state.historyLoading = false;
                state.transactionResponse = action.payload.responsData;
                state.transactionHistory = action.payload.history || [];
            })
            .addCase(getTransactionHistory.rejected, (state, action: any) => {
                state.historyLoading = false;
                state.error = action.payload?.message || "Failed to Fetch Transaction History";
                state.transactionResponse = null;
            })
            .addCase(addFund.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.addFundResponse = null;
                state.historyLoading = true;
            })
            .addCase(addFund.fulfilled, (state, action: PayloadAction<string>) => {
                state.loading = false;
                state.addFundResponse = action.payload;
            })
            .addCase(addFund.rejected, (state, action: any) => {
                state.loading = false;
                state.addFundResponse = null;
                state.error = action.payload;
            })
            .addCase(withdrawalFund.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.withdrawalResponse = null;
            })
            .addCase(withdrawalFund.fulfilled, (state, action: PayloadAction<string>) => {
                state.loading = false;
                state.withdrawalResponse = action.payload;
            })
            .addCase(withdrawalFund.rejected, (state, action: any) => {
                state.loading = false;
                state.withdrawalResponse = null;
                state.error = action.payload;
            })
            .addCase(cancelWithdrawalFundRequest.pending, (state) => {
                state.loadingCancel = true;
                state.error = "";
                state.cancelRequestResponse = null;
            })
            .addCase(cancelWithdrawalFundRequest.fulfilled, (state, action: PayloadAction<any>) => {
                state.loadingCancel = false;
                state.cancelRequestResponse = action.payload;
            })
            .addCase(cancelWithdrawalFundRequest.rejected, (state, action: any) => {
                state.loadingCancel = false;
                state.cancelRequestResponse = null;
                state.error = action.payload;
            })
            .addCase(getWithdrawalHistory.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.withdrawalHistoryResponse = null;
            })
            .addCase(getWithdrawalHistory.fulfilled, (state, action: PayloadAction<WithdrawalHistoryResponse>) => {
                state.loading = false;
                state.withdrawalHistoryResponse = action.payload.responsData;
                state.withdrawalHistory = action.payload.history || [];
            })
            .addCase(getWithdrawalHistory.rejected, (state, action: any) => {
                state.loading = false;
                state.withdrawalHistoryResponse = null;
                state.error = action.payload;
                state.withdrawalHistory = [];
            });
    },
});
export const { setReduxLoading } = fundSlice.actions

export default fundSlice.reducer;
